import { Box, Modal, Grid } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useContext, useState, useEffect, useCallback } from "react";
import AdminComponent from "./components/admin/AdminComponent";
import CalendarView from "./components/calendar/CalendarView";
import ToggleButton from "./components/common/ToggleButton";
import DashboardsContainer from "./components/dashboards/DashboardsContainer";
import DataDrawer from "./components/data-items/DataDrawer";
import HomePage from "./components/home/Home";
import { LoginModal } from "./components/login/LoginModal";
import MapComponent from "./components/map/MapComponent";
import MediaDrawer from "./components/media/MediaDrawer";
import SidePanel from "./components/side-panel/SidePanel";
import TitleBar from "./components/title-bar/TitleBar";
import BulkEditPage from "./components/upload/BulkEditPage";
import BulkUploadPage from "./components/upload/BulkUploadPage";
import { useConfig } from "./contexts/ConfigContext";
import { SelectionContext } from "./contexts/SelectionContext";
import { useUser } from "./contexts/UserContext";
import { UserInterfaceProvider, useUserInterface } from "./contexts/UserInterfaceContext";
import { useMapLibreLayers } from "./hooks/useMapLibreLayers";
import { ApiEndpoint, FlexibleDataItem } from "./types";

const PageContainer: React.FC<{
  children: React.ReactNode;
  visible: boolean;
  config: any;
}> = ({ children, visible, config }) => {
  const opacity = Math.round((config.backgroundOpacity || 0.4) * 255).toString(16).padStart(2, '0');
  return (
    <Box
      sx={{
        display: visible ? "flex" : "none",
        flexDirection: "column",
        height: "calc(100vh - 66px)",
        overflow: "hidden",
        backgroundColor: (theme) => `${theme.palette.secondary.main}${opacity}`,
        position: "relative",
        zIndex: 2,
      }}
    >
      {children}
    </Box>
  );
};

const AppContentInner: React.FC = () => {
  const { profile, oAuthHeader, isInitialized } = useUser();
  const config = useConfig();
  const { mapLibreLayersReady } = useMapLibreLayers();
  const { selectedGeoEntity, setSelectedEntity, selectedEndpoint, selectedEntity } = useContext(SelectionContext);
  const { enqueueSnackbar } = useSnackbar();
  
  // Use UserInterface context for DataDrawer state management
  const { 
    state: { dataDrawerMode, timelineHeight, endpointTimelineState, dataDrawerManuallyClosedOnMapTab }, 
    getCurrentTimelineVisibility,
    setTimelineForEndpoint,
    openDataDrawerCompact, 
    openDataDrawerFullscreen, 
    closeDataDrawer,
    setDataDrawerManuallyClosedOnMapTab
  } = useUserInterface();
  
  const isDataDrawerOpen = dataDrawerMode !== 'hidden';
  

  const [currentTab, setCurrentTab] = useState(0);

  // Effect: Open DataDrawer when on Map tab, unless user manually closed it
  useEffect(() => {
    if (currentTab === 1 && dataDrawerMode === 'hidden' && !dataDrawerManuallyClosedOnMapTab) {
      openDataDrawerCompact();
    }
  }, [currentTab, dataDrawerMode, dataDrawerManuallyClosedOnMapTab, openDataDrawerCompact]);

  // Effect: Reset manual close flag when leaving Map tab
  useEffect(() => {
    if (currentTab !== 1 && dataDrawerManuallyClosedOnMapTab) {
      setDataDrawerManuallyClosedOnMapTab(false);
    }
  }, [currentTab, dataDrawerManuallyClosedOnMapTab, setDataDrawerManuallyClosedOnMapTab]);

  const [currentOptions, setCurrentOptions] = useState<any>({});
  const [currentEndpoint, setCurrentEndpoint] = useState<ApiEndpoint | null>(null);
  
  // Use selectedEndpoint from SelectionContext as the primary source for timeline
  const activeEndpoint = selectedEndpoint || currentEndpoint;
  
  // Get timeline visibility for current endpoint (elegant!)
  const isTimelineVisible = getCurrentTimelineVisibility(activeEndpoint);
  
  // Timeline state management working correctly
  
  // Initialize timeline state for endpoints (elegant approach - runs after activeEndpoint is available)
  useEffect(() => {
    console.log('[AppContent] 🔍 useEffect triggered with activeEndpoint:', {
      hasActiveEndpoint: !!activeEndpoint,
      endpointName: activeEndpoint?.name,
      endpointType: typeof activeEndpoint?.name,
      selectedEndpoint: selectedEndpoint?.name,
      currentEndpoint: currentEndpoint?.name
    });
    
    if (!activeEndpoint?.name) {
      console.log('[AppContent] ⚠️ Early return - no activeEndpoint name');
      return;
    }
    
    const endpointName = activeEndpoint.name;
    const hasDateConfig = !!activeEndpoint.dateConfig?.hasDateFilter;
    const shouldAutoShow = !!activeEndpoint.dateConfig?.showCalendarByDefault;
    const hasExistingState = endpointTimelineState.hasOwnProperty(endpointName);
    
    // Timeline initialization logic
    
    // Only set initial state if endpoint has never been visited
    if (!hasExistingState && hasDateConfig) {
      // Initialize timeline state from configuration
      setTimelineForEndpoint(endpointName, shouldAutoShow);
    }
  }, [activeEndpoint?.name, activeEndpoint?.dateConfig, endpointTimelineState, setTimelineForEndpoint]);
  
  // Grid layout coordination working correctly
  
  const [bulkEditItemIds, setBulkEditItemIds] = useState<string[]>([]);
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);

  // Use UserInterface context for side panel state
  const { state, setSidePanelOpen } = useUserInterface();
  const isSidePanelOpen = state.isSidePanelOpen;

  const handleSidePanelClose = () => {
    setSidePanelOpen(false);
  };

  const handleSidePanelToggle = () => {
    setSidePanelOpen(!isSidePanelOpen);
  };

  // Initialize side panel state from config
  useEffect(() => {
    setSidePanelOpen(config.sidebarOpen ?? false); // Default to closed
  }, [config.sidebarOpen, setSidePanelOpen]);
  
  // Initialize default endpoint on startup
  useEffect(() => {
    if (!currentEndpoint && !selectedEndpoint && config?.apiEndpoints?.length > 0) {
      // Find default endpoint from config
      const defaultTableName = config.defaultDataTable || "tags";
      const defaultEndpoint = config.apiEndpoints.find(ep => 
        ep.tableName === defaultTableName || 
        ep.name.toLowerCase() === defaultTableName.toLowerCase()
      ) || config.apiEndpoints[0]; // Fallback to first endpoint
      
      // Initialize default endpoint from configuration
      
      setCurrentEndpoint(defaultEndpoint);
    }
  }, [currentEndpoint, selectedEndpoint, config?.apiEndpoints, config?.defaultDataTable]);

  const handleHomeCardClick = (tab: number, options: any) => {
    setCurrentTab(tab);
    setCurrentOptions(options);
    
    // If navigating to data tab (tab 2), open DataDrawer in fullscreen mode
    if (tab === 2) {
      openDataDrawerFullscreen();
    }
  };

  const handleOpenAdminModal = () => {
    setIsAdminModalOpen(true);
  };

  const handleCloseAdminModal = () => {
    setIsAdminModalOpen(false);
  };

  const handleOpenBulkEdit = (itemIds: string[]) => {
    if (itemIds.length === 0) {
      enqueueSnackbar("No items selected for bulk editing", { variant: "warning" });
      return;
    }
    
    setBulkEditItemIds(itemIds);
    setCurrentTab(4);
    // Close DataDrawer when going to bulk edit
    closeDataDrawer();
  };

  const getTabContent = (tab: number) => {
    // Check if dashboards tab is enabled (default true for backward compatibility)
    const hasDashboardsTab = config.dashboardsTab?.enabled !== false;
    const dashboardsTabIndex = hasDashboardsTab ? 5 : -1; // Dashboards is at index 5 when enabled
    
    switch (tab) {
      case 0:
        return <HomePage onCardClick={handleHomeCardClick} />;
      case 3:
        return <BulkUploadPage />;
      case 4:
        return (
          <BulkEditPage
            itemIds={bulkEditItemIds.length > 0 ? bulkEditItemIds : []}
            onSaveComplete={handleEditSaveComplete}
            onCancel={handleEditCancel}
          />
        );
      default:
        // Handle dashboards tab
        if (hasDashboardsTab && tab === dashboardsTabIndex) {
          return <DashboardsContainer />;
        }
        return null;
    }
  };

  const handleEditSaveComplete = () => {
    enqueueSnackbar("Changes saved successfully", { variant: "success" });
    setCurrentTab(2);
    setBulkEditItemIds([]);
    // Reopen DataDrawer in fullscreen mode
    openDataDrawerFullscreen();
  };

  const handleEditCancel = () => {
    setCurrentTab(2);
    setBulkEditItemIds([]);
    // Reopen DataDrawer in fullscreen mode
    openDataDrawerFullscreen();
  };

  // Timeline event handling moved to CalendarContainer

  if (!isInitialized) {
    return null;
  }

  if (!profile || !oAuthHeader) {
    return <LoginModal key="login" open={true} onClose={() => {}} />;
  }

  return (
    <Box 
      key={oAuthHeader?.Authorization} 
      sx={{ height: "100vh", position: "relative" }}
    >
      <Box sx={{ position: "relative", zIndex: 1200 }}>
        <TitleBar
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          setCurrentEndpoint={setCurrentEndpoint}
          onOpenAdminModal={handleOpenAdminModal}
          currentEndpoint={activeEndpoint}
        />
      </Box>
      
      {/* Smart Timeline - Only renders when it should show content AND no item selected in Tables view */}
      {(() => {
        const isTablesTab = currentTab === 2;
        const hasSelectedItem = !!selectedEntity;
        const shouldRender = isTimelineVisible && 
                            activeEndpoint?.dateConfig?.hasDateFilter && 
                            (currentTab === 1 || currentTab === 2); // Show timeline when enabled, regardless of item selection
        
        if (!shouldRender) {
          return null;
        }
        return (
          <CalendarView
            activeEndpoint={activeEndpoint}
            currentTab={currentTab}
            height={timelineHeight}
          />
        );
      })()}
      
      <Grid
        container
        sx={{
          position: "fixed",
          top: (() => {
            const isCalendarActuallyVisible = isTimelineVisible && 
                                            activeEndpoint?.dateConfig?.hasDateFilter && 
                                            (currentTab === 1 || currentTab === 2); // Same logic as CalendarView render condition
            
            return isCalendarActuallyVisible ? `calc(66px + ${timelineHeight})` : "66px";
          })(),
          left: 0,
          width: "100%",
          height: (() => {
            const isCalendarActuallyVisible = isTimelineVisible && 
                                            activeEndpoint?.dateConfig?.hasDateFilter && 
                                            (currentTab === 1 || currentTab === 2); // Same logic as CalendarView render condition
            
            return isCalendarActuallyVisible 
              ? `calc(100vh - 66px - ${timelineHeight})` 
              : "calc(100vh - 66px)";
          })(),
          zIndex: 1,
          transition: "all 0.3s ease-in-out"
        }}
      >
        {/* Left Column: Map + MediaDrawer */}
        <Grid 
          item 
          xs={isDataDrawerOpen && dataDrawerMode !== 'fullscreen' ? 8 : 12} // Full width when DataDrawer closed OR in fullscreen mode
          sx={{ 
            position: "relative",
            height: "100%",
            transition: "all 0.3s ease-in-out", // Smooth width transitions
        }}
      >
        <ToggleButton
          isOpen={isSidePanelOpen}
          onClick={handleSidePanelToggle}
          direction="horizontal"
          position={{
            left: "16px",
            top: "50%",
            transform: "translateY(-50%)",
          }}
          zIndex={1200}
        />

        <MapComponent isSidePanelOpen={isSidePanelOpen} />
        <SidePanel open={isSidePanelOpen} onClose={handleSidePanelClose} />
        
          {/* MediaDrawer positioned within the left column */}
          <MediaDrawer />
        </Grid>

        {/* Right Column: DataDrawer - Only rendered when DataDrawer is open AND in compact mode */}
        {isDataDrawerOpen && dataDrawerMode === 'compact' && (
          <Grid 
            item 
            xs={4} // 4/12 = 33.3% width for data area
            sx={{ 
              position: "relative",
              height: "100%",
              transition: "all 0.3s ease-in-out", // Smooth width transitions
            }}
          >
        {/* Singleton DataDrawer - always rendered, controlled by context */}
            <DataDrawer onOpenBulkEdit={handleOpenBulkEdit} options={currentOptions} />
          </Grid>
        )}
      </Grid>

      {/* DataDrawer in fullscreen mode - rendered outside main Grid */}
      {isDataDrawerOpen && dataDrawerMode === 'fullscreen' && (
        <DataDrawer onOpenBulkEdit={handleOpenBulkEdit} options={currentOptions} />
      )}
      
      {/* MediaDrawer for fullscreen mode - positioned below DataDrawer */}
      {isDataDrawerOpen && dataDrawerMode === 'fullscreen' && (
        <MediaDrawer />
      )}

      <PageContainer visible={currentTab !== 1 && currentTab !== 2} config={config}>
        {getTabContent(currentTab)}
      </PageContainer>

      <Modal
        open={isAdminModalOpen}
        onClose={handleCloseAdminModal}
        aria-labelledby="admin-panel-modal-title"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box sx={{outline: 'none'}}> 
          <AdminComponent onClose={handleCloseAdminModal} />
        </Box>
      </Modal>
    </Box>
  );
};

const AppContent: React.FC = () => {
  return (
    <UserInterfaceProvider>
      <AppContentInner />
    </UserInterfaceProvider>
  );
};

export default AppContent;
